import client from './client';
import type {
  Service,
  CloudAccount,
  CloudProvider,
  CreateServiceRequest,
  UpdateServiceRequest,
  ServiceHealth,
  PaginatedResponse,
} from '@/types';

export interface ServiceListParams {
  team_id?: string;
  service_type?: string;
  group?: string;
  /** Filter to services with at least one cloud_account in this env. */
  env?: string;
  lifecycle_stage?: string;
  search?: string;
  /** Backend sort column. cost_30d uses a LEFT JOIN aggregate. */
  sort?: string;
  /** asc | desc */
  order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export interface ServiceCostEnvBreakdown {
  environment: string;
  total_cost: number;
  currency: string;
  by_provider: Record<string, number>;
}

export interface ServiceCostByEnv {
  service_slug: string;
  service_name: string | null;
  period_start: string;
  period_end: string;
  total_cost: number;
  currency: string;
  envs: ServiceCostEnvBreakdown[];
}

export async function fetchServices(params?: ServiceListParams) {
  const { data } = await client.get<PaginatedResponse<Service>>('/services', { params });
  return data;
}

export async function fetchService(slug: string) {
  const { data } = await client.get<Service>(`/services/${slug}`);
  return data;
}

export async function createService(body: CreateServiceRequest) {
  const { data } = await client.post<Service>('/services', body);
  return data;
}

export async function updateService(slug: string, body: UpdateServiceRequest) {
  const { data } = await client.put<Service>(`/services/${slug}`, body);
  return data;
}

export async function deleteService(slug: string) {
  await client.delete(`/services/${slug}`);
}

export async function fetchServiceHealth(slug: string) {
  const { data } = await client.get<ServiceHealth>(`/services/${slug}/health`);
  return data;
}

export async function fetchServiceCostByEnv(slug: string, periodDays = 30) {
  const { data } = await client.get<ServiceCostByEnv>(
    `/services/${slug}/cost-by-env`,
    { params: { period_days: periodDays } },
  );
  return data;
}

// ---------- Cloud-account management ----------

export interface AttachCloudAccountRequest {
  provider: CloudProvider;
  account_identifier: string;
  environment: string;
  display_name?: string;
}

export async function fetchServiceCloudAccounts(slug: string) {
  const { data } = await client.get<CloudAccount[]>(`/services/${slug}/cloud-accounts`);
  return data;
}

export async function attachCloudAccount(slug: string, body: AttachCloudAccountRequest) {
  const { data } = await client.post<CloudAccount>(`/services/${slug}/cloud-accounts`, body);
  return data;
}

export async function detachCloudAccount(slug: string, accountId: string) {
  await client.delete(`/services/${slug}/cloud-accounts/${accountId}`);
}
